import { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, Clock, RefreshCw, X } from 'lucide-react';
import api from '../api/api';

const SEVERITY_CONFIG = {
  critical: { label: 'Crítica', class: 'bg-red-100 text-red-800 border border-red-200', dot: 'bg-red-500', bg: 'bg-red-50 border-red-200' },
  warning: { label: 'Aviso', class: 'bg-yellow-100 text-yellow-800 border border-yellow-200', dot: 'bg-yellow-500', bg: 'bg-yellow-50 border-yellow-200' },
  info: { label: 'Info', class: 'bg-blue-100 text-blue-800 border border-blue-200', dot: 'bg-blue-500', bg: 'bg-blue-50 border-blue-200' },
};

export default function Alertas() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');

  const load = () => {
    setLoading(true);
    api.get(`/alerts?status=${filter}`)
      .then(r => setAlerts(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter]);

  const handleUpdate = async (id, status) => {
    await api.put(`/alerts/${id}?status=${status}`);
    load();
  };

  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;

  return (
    <div className="p-6 md:p-8 page-enter">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Alertas de Mantenimiento</h1>
          <p className="text-sm text-gray-500 mt-0.5">{criticalCount} críticas · {warningCount} avisos</p>
        </div>
        <button onClick={load} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500" data-testid="refresh-alerts">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Summary badges */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle size={20} className="text-red-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-red-700" style={{ fontFamily: 'Barlow, sans-serif' }}>{criticalCount}</p>
            <p className="text-xs text-red-600">Críticas</p>
          </div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
            <Clock size={20} className="text-yellow-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-yellow-700" style={{ fontFamily: 'Barlow, sans-serif' }}>{warningCount}</p>
            <p className="text-xs text-yellow-600">Avisos</p>
          </div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle size={20} className="text-green-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-green-700" style={{ fontFamily: 'Barlow, sans-serif' }}>{alerts.length}</p>
            <p className="text-xs text-green-600">Total activas</p>
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-5">
        {[
          { key: 'active', label: 'Activas' },
          { key: 'acknowledged', label: 'Reconocidas' },
          { key: 'resolved', label: 'Resueltas' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors
              ${filter === f.key ? 'bg-[#16a34a] text-white border-[#16a34a]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
            data-testid={`alert-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Alerts list */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#16a34a]" />
        </div>
      ) : alerts.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 text-center">
          <CheckCircle size={40} className="text-green-400 mx-auto mb-3" />
          <p className="text-gray-500 text-sm font-medium">No hay alertas {filter === 'active' ? 'activas' : filter === 'acknowledged' ? 'reconocidas' : 'resueltas'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map(alert => {
            const cfg = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
            return (
              <div
                key={alert.id}
                className={`bg-white rounded-xl border shadow-sm p-5 hover:shadow-md transition-all ${alert.severity === 'critical' ? 'border-l-4 border-l-red-500' : alert.severity === 'warning' ? 'border-l-4 border-l-yellow-500' : ''}`}
                data-testid={`alert-card-${alert.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${cfg.dot}`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.class}`}>{cfg.label}</span>
                        <span className="text-xs text-gray-400 font-mono">{alert.equipment_code}</span>
                      </div>
                      <h3 className="font-semibold text-gray-900 text-sm">{alert.title}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{alert.equipment_name}</p>
                      <p className="text-xs text-gray-400 mt-1">{alert.description}</p>
                      <div className="flex gap-4 mt-2 text-[11px] text-gray-400">
                        <span>Horas actuales: <span className="font-mono font-medium text-gray-600">{alert.current_hours}</span></span>
                        <span>Umbral: <span className="font-mono font-medium text-gray-600">{alert.trigger_hours} h</span></span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="text-[10px] text-gray-400">
                      {alert.created_at ? new Date(alert.created_at).toLocaleDateString('es-ES') : ''}
                    </span>
                    {alert.status === 'active' && (
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleUpdate(alert.id, 'acknowledged')}
                          className="text-[10px] px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors"
                          data-testid={`ack-alert-${alert.id}`}
                        >
                          Reconocer
                        </button>
                        <button
                          onClick={() => handleUpdate(alert.id, 'resolved')}
                          className="text-[10px] px-2 py-0.5 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                          data-testid={`resolve-alert-${alert.id}`}
                        >
                          Resolver
                        </button>
                      </div>
                    )}
                    {alert.status === 'acknowledged' && (
                      <button
                        onClick={() => handleUpdate(alert.id, 'resolved')}
                        className="text-[10px] px-2 py-0.5 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                      >
                        Resolver
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
